// Grounded value pills — one per observed distinct value, count-anchored. Presentational: it reflects
// `selected` and calls `onPick(value)`; the panel owns the operator the selection implies. Folded
// canonical values carry an "N spellings" badge whose tooltip lists the raw spellings (one per line).
// Pill styling matches the L1 Input/Button interaction states (see pill-styles.ts).

import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';
import { cn } from '@/_shared/lib/utils';

import { useRovingListFocus } from './hooks/useRovingListFocus';
import { InsightSummary } from './InsightSummary';
import { isTotalEstimated } from './lib/coverage-text';
import { formatCount } from './lib/format';
import {
  PILL_BASE,
  PILL_COUNT,
  PILL_COUNT_SELECTED,
  PILL_COUNT_UNSELECTED,
  PILL_SELECTED,
  PILL_UNSELECTED,
} from './lib/pill-styles';
import { pillRaws, type PillValue } from './types';

const SPELLINGS_BADGE =
  'rounded border border-transparent bg-muted px-1 text-[0.625rem] font-normal text-muted-foreground';

export function EnumValuePills({
  values,
  total,
  totalAccuracy,
  selected,
  onPick,
  ariaLabel,
  negate = false,
}: {
  values: PillValue[];
  total?: number;
  /** The catalog's confidence in `total`, surfaced verbatim from the row — see {@link isTotalEstimated}. */
  totalAccuracy?: string;
  selected: string[];
  onPick: (pill: PillValue) => void;
  ariaLabel: string;
  negate?: boolean; // `is not one of` / `is not` — the audience is the rows the selection EXCLUDES
}) {
  const { refs, roving, setFocusIndex, onKeyDown } = useRovingListFocus(values.length);

  const selectedSet = new Set(selected);
  // A pill (or folded group) counts as selected only when every raw value it stands for is selected.
  // A truncated pill is never selected — it's a display-only fragment, never a member of the match.
  const isPillSelected = (v: PillValue) => v.truncated !== true && pillRaws(v).every((r) => selectedSet.has(r));
  const selectedPills = values.filter(isPillSelected);
  // The sum is knowable only when EVERY selected pill's count is — one unmeasured pill (below the
  // probe's top-K cutoff) makes the sum a lower bound, and a lower bound presented as a count is the
  // same lie in smaller print. `undefined` here means "not known", same as an unselected control.
  const selectedCount = selectedPills.every((v) => v.count !== undefined)
    ? selectedPills.reduce((sum, v) => sum + (v.count ?? 0), 0)
    : undefined;
  // Positive ops keep exactly the selected rows; negation keeps everyone the selection EXCLUDES (total −
  // selected). Without a known `total` the negated match count is unknowable, so show the exclusion label
  // rather than presenting the excluded count as if it were the match.
  const negatedWithoutTotal = negate && total === undefined;
  const matchCount =
    negate && total !== undefined && selectedCount !== undefined ? total - selectedCount : selectedCount;
  const softTotal = isTotalEstimated(total, totalAccuracy);
  // Negation derives the match as `total − selected`, so a soft denominator makes the COUNT soft too. A
  // positive operator's count is the sum of the selected pills' own measured counts and stays firm — which
  // is why this cannot be one flag for the whole line.
  const isMatchEstimated = negate && softTotal;

  return (
    <div className="flex flex-col gap-2">
      <InsightSummary
        matchCount={matchCount}
        total={total}
        totalEstimated={softTotal}
        estimated={isMatchEstimated}
        unset={negatedWithoutTotal}
        unsetLabel="Excludes the selected values"
      />
      <div
        role="listbox"
        // Always: a pick is a toggle whatever the operator currently reads, and the operator follows.
        aria-multiselectable
        aria-label={ariaLabel}
        className="flex flex-wrap gap-1.5 p-0.5"
      >
        {values.map((v, i) => {
          const isSelected = isPillSelected(v);
          const spellings = v.mergedFrom ?? [];
          const isTruncated = v.truncated === true;
          const pill = (
            <button
              key={v.value}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="option"
              aria-selected={isSelected}
              // `aria-disabled`, not `disabled`: the value stays visible AND focusable (a disabled button
              // drops out of the roving tab order), it just cannot be committed.
              aria-disabled={isTruncated || undefined}
              tabIndex={i === roving ? 0 : -1}
              onFocus={() => setFocusIndex(i)}
              onKeyDown={onKeyDown}
              onClick={() => {
                if (!isTruncated) onPick(v);
              }}
              className={cn(
                PILL_BASE,
                isSelected ? PILL_SELECTED : PILL_UNSELECTED,
                isTruncated && 'cursor-not-allowed opacity-60',
              )}
            >
              <span>{v.label}</span>
              {spellings.length > 0 && (
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className={SPELLINGS_BADGE}>
                      {spellings.length} {spellings.length === 1 ? 'spelling' : 'spellings'}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex flex-col gap-0.5">
                      {spellings.map((m) => (
                        <span key={m.raw} className="flex items-baseline justify-between gap-4 tabular-nums">
                          <span>{m.raw}</span>
                          {m.count !== undefined && <span className="opacity-70">{formatCount(m.count)}</span>}
                        </span>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
              {!isTruncated && v.count !== undefined && (
                <span className={cn(PILL_COUNT, isSelected ? PILL_COUNT_SELECTED : PILL_COUNT_UNSELECTED)}>
                  {formatCount(v.count)}
                </span>
              )}
            </button>
          );
          // Disable-with-a-reason: the pill says WHY it is inert rather than just looking broken.
          //
          // The ordinary pill is returned BARE — no wrapper element. A `<span>` around it would make
          // `role="listbox"` have a non-`option` child and fail axe's `aria-required-children`, asymmetrically
          // (Radix `Slot` emits no DOM of its own, so the truncated branch renders the button directly while a
          // wrapped branch would not). Testing-library still finds both either way — roles do not depend on
          // ancestry — so the tests would pass and hide it. Put the key on the button instead.
          return isTruncated ? (
            <Tooltip key={v.value} delayDuration={200}>
              <TooltipTrigger asChild>{pill}</TooltipTrigger>
              <TooltipContent>
                This value was shortened when it was sampled, so it can&apos;t be selected.
              </TooltipContent>
            </Tooltip>
          ) : (
            pill
          );
        })}
      </div>
    </div>
  );
}
